import { useState, useEffect, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PriceChange as PriceChangeIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { Header } from "components/shared";
import { useUtility } from "utils/context/UtilityContext";
import { GetProductsQuery, Product, SetFlag } from "utils";
import { useDebounce } from "utils/hooks/useDebounce";
import {
  deleteProduct,
  getProducts,
} from "utils/api/private/ProductApiService";
import { ProductDialog } from "./ProductDialog";
import { PricingDialog } from "./PricingDialog";
import { Chip } from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";

const formatLocalPrice = (price: number | string, currency: string) => {
  const amount = typeof price === "string" ? Number(price) : price;
  const code = currency === "€" ? "EUR" : currency;
  try {
    if (Number.isFinite(amount)) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: code as any,
      }).format(amount as number);
    }
  } catch {}
  return `${price} ${currency}`;
};

export const ProductManagement = () => {
  const { marginBottomHeader } = useUtility();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Server-side state
  const [query, setQuery] = useState<GetProductsQuery>({
    page: 1,
    limit: 10,
    sortBy: "name",
    order: "asc",
  });
  const [total, setTotal] = useState(0);
  const debouncedSearch = useDebounce(query.search || "", 500);

  // Modal states
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getProducts({ ...query, search: debouncedSearch });
      setProducts(response.data);
      setTotal(response.meta.total);
    } catch (err: any) {
      setError(err.message || "Failed to load products.");
    } finally {
      setLoading(false);
    }
  }, [query, debouncedSearch]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSort = (property: string) => {
    const isAsc = query.sortBy === property && query.order === "asc";
    setQuery((prev) => ({
      ...prev,
      sortBy: property,
      order: isAsc ? "desc" : "asc",
    }));
  };

  const handleDelete = async (productId: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        fetchProducts(); // Refresh
      } catch (err: any) {
        setError(err.message || "Failed to delete product");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`${marginBottomHeader} p-4`}
    >
      <Header secondPart={false} />
      <Box sx={{ width: "100%", p: { xs: 1, md: 2 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4">Product Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedProduct(null);
              setProductModalOpen(true);
            }}
          >
            Add Product
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search..."
                value={query.search || ""}
                onChange={(e) =>
                  setQuery((prev) => ({
                    ...prev,
                    search: e.target.value,
                    page: 1,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={query.category || ""}
                  label="Category"
                  onChange={(e) =>
                    setQuery((prev) => ({
                      ...prev,
                      category: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Gaming">Gaming</MenuItem>
                  <MenuItem value="Services">Services</MenuItem>
                  <MenuItem value="Musique et streaming">
                    Music & Streaming
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={query.country || ""}
                  label="Country"
                  onChange={(e) =>
                    setQuery((prev) => ({
                      ...prev,
                      country: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="US">USA</MenuItem>
                  <MenuItem value="FR">France</MenuItem>
                  <MenuItem value="BE">Belgium</MenuItem>
                  <MenuItem value="CD">DRC</MenuItem>
                  <MenuItem value="ZA">South Africa</MenuItem>
                  <MenuItem value="AU">Australia</MenuItem>
                  <MenuItem value="global">Global</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
  <TableRow>
    <TableCell>
      <TableSortLabel
        active={query.sortBy === "name"}
        direction={query.sortBy === "name" ? query.order : "asc"}
        onClick={() => handleSort("name")}
      >
        Name
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={query.sortBy === "provider"}
        direction={query.sortBy === "provider" ? query.order : "asc"}
        onClick={() => handleSort("provider")}
      >
        Provider
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={query.sortBy === "category"}
        direction={query.sortBy === "category" ? query.order : "asc"}
        onClick={() => handleSort("category")}
      >
        Category
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={query.sortBy === "country"}
        direction={query.sortBy === "country" ? query.order : "asc"}
        onClick={() => handleSort("country")}
      >
        Country
      </TableSortLabel>
    </TableCell>

    <TableCell align="right">
      <TableSortLabel
        active={query.sortBy === "price"}
        direction={query.sortBy === "price" ? query.order : "asc"}
        onClick={() => handleSort("price")}
      >
        Price (currency)
      </TableSortLabel>
    </TableCell>

    <TableCell align="right">
      <TableSortLabel
        active={query.sortBy === "priceUSD"}
        direction={query.sortBy === "priceUSD" ? query.order : "asc"}
        onClick={() => handleSort("priceUSD")}
      >
        Price (BSB Coins)
      </TableSortLabel>
    </TableCell>

    <TableCell>
      <TableSortLabel
        active={query.sortBy === "isBestseller"}
        direction={query.sortBy === "isBestseller" ? query.order : "asc"}
        onClick={() => handleSort("isBestseller")}
      >
        Bestseller
      </TableSortLabel>
    </TableCell>

    <TableCell>Actions</TableCell>
  </TableRow>
</TableHead>


              <TableBody>
                {loading ? (
                  <TableRow>
                    {/* 8 visible columns now */}
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.provider}</TableCell>
                      <TableCell>{p.category}</TableCell>
                      <TableCell>
                        <SetFlag
                          countryCode={p.countryCode}
                          countryName={p.country}
                        />
                      </TableCell>

                      {/* Local/base price with currency */}
                      <TableCell align="right">
                        {formatLocalPrice(p.price as any, String(p.currency))}
                      </TableCell>

                      {/* BSB coins */}
                      <TableCell align="right">
                        {typeof p.priceUSD === "number"
                          ? p.priceUSD.toFixed(2)
                          : "--"}
                      </TableCell>

                      {/* Bestseller chip */}
                      <TableCell>
                        {p.isBestseller ? (
                          <Chip
                            icon={<Star sx={{ fontSize: 18 }} />}
                            color="success"
                            label="Yes"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            icon={<StarBorder sx={{ fontSize: 18 }} />}
                            color="default"
                            label="No"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Tooltip title="Edit Product">
                          <IconButton
                            onClick={() => {
                              setSelectedProduct(p);
                              setProductModalOpen(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Pricing">
                          <IconButton
                            onClick={() => {
                              setSelectedProduct(p);
                              setPricingModalOpen(true);
                            }}
                          >
                            <PriceChangeIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleDelete(p.id)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={query.limit}
            page={query.page - 1}
            onPageChange={(e, newPage) =>
              setQuery((prev) => ({ ...prev, page: newPage + 1 }))
            }
            onRowsPerPageChange={(e) =>
              setQuery((prev) => ({
                ...prev,
                limit: parseInt(e.target.value, 10),
                page: 1,
              }))
            }
          />
        </Paper>
      </Box>
      <ProductDialog
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSave={fetchProducts}
        product={selectedProduct}
      />
      <PricingDialog
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        onSave={fetchProducts}
        product={selectedProduct}
      />
    </motion.div>
  );
};
